import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import Iconify from "minimals-template/components/Iconify"
import { HighlightBox } from "lib/@components/highlightBox"
import { blockLinkButtonSx, linkedButtonSx } from "./component-link-button-styles"
import { preventPropagation } from "lib/prevent"

const FIELD = "notes"
const ICON_SX = { width: 14, height: 14 }

/**
 * Slot plug for TaskNotes.
 *
 * - No componentRef: renders the default notes editor
 * - ComponentRef set, field not linked: puzzle icon toggles field on (if component has notesHtml)
 * - Field linked: shows inherited notesHtml + link icon to unlink
 */
export function ComponentRefNotes({ originalchildren }) {
    const { componentRef, linkedFields, onEnableField, onDisableField, editMode, componentTask } = useBoundContext()
    const isLinked = !!componentRef && linkedFields?.[FIELD]

    if (!isLinked) {
        const canLink = !!componentRef && componentHasNotes(componentTask)

        return (
            <Box sx={{ position: "relative" }}>
                {originalchildren}
                {editMode && canLink && (
                    <Box sx={{ position: "absolute", top: "50%", left: -28, transform: "translateY(-50%)", zIndex: 1 }}>
                        <Tooltip title="Link notes from component">
                            <IconButton
                                size="small"
                                onClick={preventPropagation(handleEnable)}
                                className="component-link-btn"
                                sx={blockLinkButtonSx}
                                aria-label="Link notes"
                            >
                                <Iconify icon="mdi:puzzle-plus-outline" sx={ICON_SX} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        )
    }

    if (componentTask === IS_LOADING) {
        return (
            <Box display="flex" justifyContent="center" sx={{ py: 2 }}>
                <CircularProgress size={20} />
            </Box>
        )
    }

    if (!componentTask) return originalchildren ?? null

    return (
        <HighlightBox path="note">
            <Box className="styled-bullets" sx={{ opacity: editMode ? 0.6 : 1, position: "relative", mt: 1 }}>
                <Tooltip title={`Inherited from: ${componentTask.heading ?? componentRef}`}>
                    <Box>
                        <Typography component="div" variant="overline" gutterBottom>
                            Notes
                        </Typography>
                        <Typography
                            component="div"
                            variant="body2"
                            dangerouslySetInnerHTML={{ __html: componentTask.notesHtml ?? "" }}
                        />
                    </Box>
                </Tooltip>
                {editMode && (
                    <Box sx={{ position: "absolute", top: 0, right: -4 }}>
                        <IconButton
                            size="small"
                            onClick={preventPropagation(handleDisable)}
                            sx={linkedButtonSx}
                            aria-label="Unlink notes"
                        >
                            <Iconify icon="mdi:link-variant" sx={ICON_SX} />
                        </IconButton>
                    </Box>
                )}
            </Box>
        </HighlightBox>
    )

    function handleEnable() {
        onEnableField?.(FIELD)
    }

    function handleDisable() {
        onDisableField?.(FIELD)
    }
}

function componentHasNotes(task) {
    if (!task || task === IS_LOADING) return false
    return !!task.notesHtml
}
